import {
  FaCheckCircle,
  FaClipboardCheck,
  FaRegClock,
  FaShieldAlt,
  FaStethoscope,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";

const seo = {
  title: "Virtual Medical Scribe Services | Physicians Success Partners",
  description:
    "Improve efficiency and reduce administrative workload with Physicians Success Partners' expert virtual medical scribe services.",
  keywords:
    "virtual medical scribe, clinical documentation support, remote scribe service",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Virtual Medical Scribe Services",
      url: "https://psprcm.us/services/virtual-medical-scribe/",
      description:
        "Improve efficiency and reduce administrative workload with Physicians Success Partners' expert virtual medical scribe services.",
      serviceType: "Virtual Medical Scribe",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Virtual Medical Scribe Solutions",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Real-Time Clinical Documentation" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "EHR Charting & Data Entry" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "SOAP Notes Preparation" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Medical Transcription Support" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Workflow Optimization & Physician Support" } },
        ],
      },
    },
    createBreadcrumbSchema("/services/virtual-medical-scribe", "Virtual Medical Scribe Services"),
  ],
};

export default function VirtualMedicalScribe() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/virtual-medical-scribe"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
        heading: "Virtual Medical Scribe Service",
        paragraph:
          "Lighten documentation pressure with virtual scribe support that helps providers stay focused on patient care and chart completion.",
        image: "/img/services-img/virtual-medical-scribe.webp",
      }}
      grid={{
        kicker: "Documentation Support",
        heading: "Give Providers More Time Back During the Clinical Day",
        description:
          "Charting demands can slow down providers, extend after-hours work, and reduce time available for patient care. Our virtual medical scribe service helps practices improve documentation flow so records stay organized while providers remain more focused on the visit itself.",
        metrics: [
          { icon: <FaStethoscope />, value: "More", label: "Provider Focus Time", style: "metricPrimary" },
          { icon: <FaRegClock />, value: "Less", label: "After-Hours Charting", style: "metricAccent" },
          { icon: <FaClipboardCheck />, value: "Clearer", label: "Documentation Flow", style: "metricLight" },
          { icon: <FaShieldAlt />, value: "100%", label: "Secure Workflow Support", style: "metricDark" },
        ],
        panelEyebrow: "Clinical Efficiency",
        panelHeading: "We help providers stay focused on care by supporting organized documentation throughout the patient encounter workflow.",
        assurances: [
          { icon: <FaCheckCircle />, label: "Structured Notes" },
          { icon: <FaStethoscope />, label: "Provider-Focused Support" },
          { icon: <FaFileInvoiceDollar />, label: "Billing-Ready Documentation" },
        ],
      }}
      services={{
        heading: "Advantages of PSP Virtual Medical Scribe Services",
        description:
          "Virtual medical scribes help providers reduce time spent on charting while supporting more complete and organized documentation. We work alongside your clinical workflow to improve efficiency without adding in-office staffing strain.",
        items: [
          { title: "Reduced Documentation Burden", text: "Our scribe support helps providers spend less time on charts and more time on patient interaction and clinical decision-making." },
          { title: "Improved Workflow Efficiency", text: "With documentation support built into the visit process, teams can move through appointments and chart completion more smoothly." },
          { title: "More Consistent Notes", text: "We help practices maintain clearer and more organized documentation that supports both care continuity and billing readiness." },
          { title: "Flexible Staffing Support", text: "Virtual delivery reduces the need for on-site scribe staffing while still giving providers dependable documentation assistance." },
        ],
      }}
      faq={{
        image: "/img/services-img/virtual-medical-scribe.webp",
        imageAlt: "Virtual medical scribe support",
        title: "Why Choose PSP for Virtual Medical Scribe Services?",
        question: [
          "How does a virtual medical scribe help providers save time?",
          "Can this service improve chart completion consistency?",
          "Will providers still stay in control of documentation?",
        ],
        answer: [
          "Virtual scribes reduce charting burden by helping document encounters efficiently, giving providers more time to focus on patients and clinical priorities.",
          "Yes. Scribe support helps maintain clearer, more organized notes that are easier to finalize and review.",
          "Yes. Providers remain fully in control of the clinical record while receiving structured documentation assistance throughout the workflow.",
        ],
      }}
      specialtiesContent={{
        eyebrow: "Documentation Support",
        heading: "Virtual scribe support designed to keep specialty providers focused on care.",
        description:
          "We help practices reduce charting pressure with flexible documentation workflows that fit modern clinical operations.",
        }}
      />
    </>
  );
}
